import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { TargetColumn } from "../../types/formBuilder.types";

interface ColumnMapperProps {
    value: string;
    columns: TargetColumn[];
    compatibleColumnTypes: string[];
    onChange: (value: string) => void;
}

// PRIM-004: this whole field previously bypassed i18n entirely -- "Column mapping" and its
// helper text were plain hardcoded English JSX strings (never passed through `t()`), so no
// translation package could ever localize them regardless of content. Column labels are now
// also run through translateMetadataLabel, matching the same fix applied to the Fluss builder
// (PRIM-003/008) for the same reason: metadata labels are often themselves translation keys.
const ColumnMapper = ({ value, columns, compatibleColumnTypes, onChange }: ColumnMapperProps) => {
    const { t } = useTranslation();
    const compatibleColumns = columns.filter((column) =>
        compatibleColumnTypes.length === 0 || compatibleColumnTypes.includes(column.columnType)
    );
    const selectedColumn = columns.find((column) => column.name === value) || null;
    const columnLabel = (column: TargetColumn) => translateMetadataLabel(t, column) || column.label || column.name;

    return (
        <Autocomplete
            size="small"
            options={compatibleColumns}
            value={selectedColumn}
            onChange={(_, column) => onChange(column?.name || "")}
            getOptionLabel={(column) => `${columnLabel(column)} (${column.name})`}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t("Column mapping")}
                    helperText={compatibleColumns.length
                        ? t("Compatible with the selected field type.")
                        : t("Set target table to load compatible columns.")}
                />
            )}
        />
    );
};

export default ColumnMapper;
